// ANGEL FLIGHT — credit-gated campaign progression.
//
// The campaign is four hand-authored missions (missions/index.ts MISSIONS =
// [overpass, nightRaid, treeline, whiteout]). Per the owner spec, Mission 1
// (campaign idx 0) is FREE; Missions 2/3/4 are CREDIT UNLOCKS — gated NOT by the
// local missionProgress chain but by a one-time credit purchase (an entitlement).
// The ENDLESS "Last Stand" mode is NEVER part of the campaign (missionIndexOf →
// -1) and is NEVER gated.
//
// All numbers come from the central /platform-config.json via the Platform SDK,
// so retuning is a one-file edit; the FALLBACK below mirrors that config so the
// game still gates correctly BEFORE the config loads (and offline). This module
// mirrors echoloop/src/progression.ts in shape — it is the reference gate.

import { Platform, type ChapterConfig, type GameConfig, type UnlockResult } from './platform';

export const GAME_ID = 'gunner';
export type Chapter = ChapterConfig;

// Mirrors hub/platform-config.json games.gunner. freeLevels = 1 (Mission 1 only);
// each later mission is one credit-unlocked chapter keyed to its campaign index
// via `from` (0-based): mission_2 gates idx 1, mission_3 idx 2, mission_4 idx 3.
const FALLBACK: GameConfig = {
  freeLevels: 1,
  chapters: [
    { sku: 'mission_2', from: 1, price: 15, title: 'MISSION 2 · NIGHT RAID' },
    { sku: 'mission_3', from: 2, price: 25, title: 'MISSION 3 · THE TREELINE' },
    { sku: 'mission_4', from: 3, price: 40, title: 'MISSION 4 · WHITEOUT' },
  ],
};

function cfg(): GameConfig {
  return Platform.gameConfig(GAME_ID, FALLBACK);
}

function fullSku(sku: string): string {
  return `${GAME_ID}:${sku}`;
}

export function getChapters(): Chapter[] {
  return cfg().chapters;
}

/** The chapter (credit unlock) gating campaign mission `idx`, or null if it sits
 *  in the free tier (idx < freeLevels) — or if `idx` is not a campaign mission
 *  (e.g. endless's -1), which is never gated. */
export function chapterForIndex(idx: number): Chapter | null {
  const c = cfg();
  // Endless / unknown (idx < 0) and the free tier are never gated.
  if (idx < 0 || idx < c.freeLevels) return null;
  let found: Chapter | null = null;
  for (const ch of c.chapters) {
    if (ch.from <= idx) found = ch;
  }
  return found;
}

/** The full SKU for a campaign index, or null if it's free / not gated. */
export function skuForIndex(idx: number): string | null {
  const ch = chapterForIndex(idx);
  return ch === null ? null : ch.sku;
}

/** Is this campaign mission playable? Endless (idx -1) + the free tier are always
 *  unlocked; while the master switch is on NOTHING is gated; otherwise a locked
 *  mission needs its chapter entitlement. */
export function isMissionUnlocked(idx: number): boolean {
  // Master switch: while unlockAll is on, the system runs but nothing is gated.
  if (Platform.flags.unlockAll) return true;
  const ch = chapterForIndex(idx);
  if (ch === null) return true; // free tier or endless
  return Platform.hasEntitlement(fullSku(ch.sku));
}

/** Spend credits to unlock a mission's chapter (idempotent: 'owned' if already
 *  held, 'insufficient' if the balance can't cover it). */
export function unlockMission(ch: Chapter): UnlockResult {
  return Platform.unlock(fullSku(ch.sku), ch.price);
}

/** Pay out the first-clear bonus for a campaign mission (no-op on replays). The
 *  earn event MUST be '<gameId>:clear:<integer>' — the server pays a flat
 *  creditsPerClear and rejects any non-integer tail. Returns the credits granted
 *  (0 on a replay / anon, when the grant is a no-op). */
export function rewardMissionClear(idx: number): number {
  const amount = Platform.limits.creditsPerClear;
  return Platform.earnOnce(`${GAME_ID}:clear:${idx}`, amount) ? amount : 0;
}
